import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress
} from '@material-ui/core';

import history from "../../history";
import { applyJob, saveJob } from "../../actions/userActions";
import client from "../../utils/client";
import useModal from "../hooks/useModal";
import ApplyForm from "./ApplyForm";
import MailPreview from "../common/MailPreview";
import Alert from "../../utils/alert";

const useStyles = makeStyles((theme) => ({
  mainSection: {
    marginTop: '5rem',
  },
  container: {
    marginBottom: '4rem',
  },
  heading: {
    textAlign: 'center',
    color: '#52bbf8',
    marginBottom: '5rem',
    fontSize: '3rem',
    fontWeight: 700,
  },
  sectionTitle: {
    fontSize: '2.2rem',
    fontWeight: 700,
    marginBottom: '2rem',
    color: '#26344a',
  },
  jobDetailsCard: {
    backgroundColor: '#f5f5f5',
    padding: '2rem',
    marginBottom: '4rem',
    borderRadius: '8px',
  },
  jobTitle: {
    fontSize: '2.4rem',
    fontWeight: 700,
    color: '#3273dc',
    marginBottom: '1rem',
  },
  jobDetails: {
    fontSize: '1.6rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90%',
      maxHeight: '90vh',
      width: '90%',
    },
  },
  dialogTitle: {
    fontSize: '1.8rem',
    fontWeight: 600,
    color: '#26344a',
  },
  dialogContent: {
    padding: '2rem',
  },
  dialogActions: {
    padding: '1rem 2rem 2rem',
    justifyContent: 'flex-start',
    gap: '1rem',
  },
  sendButton: {
    backgroundColor: '#3273dc',
    color: '#fff',
    fontSize: '1.6rem',
    padding: '0.75rem 2rem',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#2759a5',
    },
  },
  cancelButton: {
    backgroundColor: '#ff6b6b',
    color: '#fff',
    fontSize: '1.6rem',
    padding: '0.75rem 2rem',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#ff5252',
    },
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem',
  },
  divider: {
    margin: '1.5rem 0',
  },
}));

const ApplyJob = props => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(null);
  const [sending, setSending] = useState(false);

  const [openModal, closeModal, modalIsOpen] = useModal();

  let jobId = props.match.params.id;
  const jobDetails = useSelector(state => state.jobDetails.jobDetails);
  const user = useSelector(state => state.user.currentUser);
  const savedJobList = useSelector(state => state.user.savedJobList);

  const handleSubmit = fd => {
    setFormData(fd);
    openModal();
  };

  const sendEmail = () => {
    let fd = new FormData();
    for (var key in formData) {
      if (key === "attachments") {
        for (const file of formData[key]) {
          fd.append("attachment", file, file.name);
        }
      } else {
        fd.append(key, formData[key]);
      }
    }

    setSending(true);

    client
      .post(`job/${jobDetails._id}/mail`, fd, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })
      .then(res => {
        Alert.success("メールを送信しました");
        if (savedJobList.findIndex(savedJob => savedJob.job === jobId) >= 0) {
          dispatch(applyJob(user._id, jobId));
        } else {
          dispatch(saveJob(user._id, jobId, true));
        }
        history.push("/jobs/" + jobId);
      })
      .catch(error => {
        console.log(error);
        setSending(false);
      });
  };

  if (!jobDetails || jobDetails._id !== jobId) {
    console.log("Invalid params");
    history.push("/");
    return "";
  }

  return (
    <Box component="section" className={classes.mainSection}>
      <Container className={classes.container}>
        <Typography variant="h1" className={classes.heading}>
          求人に応募する
        </Typography>

        <Typography variant="h2" className={classes.sectionTitle}>
          求人詳細
        </Typography>

        <Paper className={classes.jobDetailsCard} elevation={1}>
          <Typography variant="h3" className={classes.jobTitle}>
            {jobDetails.title}
          </Typography>
          <Typography variant="body1" className={classes.jobDetails}>
            {jobDetails.details}
          </Typography>
        </Paper>

        <Typography variant="h2" className={classes.sectionTitle}>
          メールフォーム
        </Typography>

        <ApplyForm
          job={jobDetails}
          onSubmit={handleSubmit}
          email={user.email}
          title={`RE:${jobDetails.title}`}
        />

        <Dialog
          open={modalIsOpen}
          onClose={closeModal}
          className={classes.dialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle className={classes.dialogTitle}>
            以下の内容でメールを送信します。よろしいですか？
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <Divider className={classes.divider} />
            <MailPreview formData={formData} />
            <Divider className={classes.divider} />
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            {sending ? (
              <Box className={classes.spinner}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={sendEmail}
                  className={classes.sendButton}
                >
                  送信
                </Button>
                <Button
                  variant="contained"
                  onClick={closeModal}
                  className={classes.cancelButton}
                >
                  キャンセル
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ApplyJob;
